import { useNavigate } from 'react-router-dom';
import { DiagnosticContext, useOutsideClick, useSessionStorage } from '../../helpers/functions';
import { competenceLevels, education, getEducation } from '../../services/londi-constants';
import Tabs from '../Tabs';
import TestProcedure from './TestProcedure';
import { useContext, useRef, useState } from 'react';
import Container from '../../elements/container';
import PageHeading from '../../elements/page-heading';
import Alert from '../../elements/alert';
import Select from '../select';
import Button from '../../elements/button';
import SelfAssessmentGroup from './SelfAssessmentGroup';

function PromotionTest() {
  const { state, dispatch } = useContext(DiagnosticContext);
  const [selfAssessment] = useSessionStorage('selfAssessment', false);
  const [testProcedure] = useState(true);
  const [checkedReading, setCheckedReading] = useSessionStorage('checkboxesReading', false);
  const [checkedWriting, setCheckedWriting] = useSessionStorage('checkboxesWriting', false);
  const [disableWriting, setDisableWriting] = useSessionStorage('checkboxesDisableWriting', false);
  const [checkedCounting, setCheckedCounting] = useSessionStorage('checkboxesCounting', false);
  const [disableCounting, setDisableCounting] = useSessionStorage('checkboxesDisableCounting', false);
  const [educationError, setEducationError] = useState(false);
  const [readingError, setReadingError] = useState(false);
  const [writingError, setWritingError] = useState(false);
  const [countingError, setCountingError] = useState(false);
  const [disableReading, setDisableReading] = useSessionStorage('checkboxesDisableReading', false);
  const [selectReadingGroup, setSelectReadingGroup] = useSessionStorage('readingGroup', null);
  const [selectWritingGroup, setSelectWritingGroup] = useSessionStorage('writingGroup', null);
  const [selectCountingGroup, setSelectCountingGroup] = useSessionStorage('countingGroup', null);
  const [MandatoryError, setMandatoryError] = useState(false);
  const { NotSpecified: _, ...specifiedCompetenceLevels } = competenceLevels;
  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef);
  const educationRef = useRef(null);
  const testsRef = useRef(null);
  const readingRef = useRef(null);
  const writingRef = useRef(null);
  const countingRef = useRef(null);
  const navigate = useNavigate();

  const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const onSubmit = () => {
    if (state.education == null) {
      setEducationError(true);
      scrollTo(educationRef);
      return;
    }

    if (!checkedReading && !checkedWriting && !checkedCounting) {
      dispatch({ type: 'screenResultsEstimated', payload: true });
      navigate('/self-assessment');
      return;
    }

    if (selfAssessment) {
      navigate('/funding-recomendations');
      return;
    }

    navigate('/test-results');
  };

  const onSkip = () => {
    if (state.education == null) {
      setEducationError(true);
      scrollTo(educationRef);
      return;
    }
    setMandatoryError(false);
    setReadingError(false);
    setWritingError(false);
    setCountingError(false);
    navigate('/self-assessment');
  };

  return (
    <>
      <Tabs />
      <Container className="flex flex-col items-center">
        <PageHeading stretch title="Testauswahl" />
        <div className="max-w-6xl py-6 space-y-4 -mt-6">
          <p className="text-lg leading-8 text-gray-600">
            Bitte wählen Sie aus, welche Testverfahren Sie verwendet haben. Alternativ können Sie als Fachkraft auch selbst einschätzen wie die
            Fertigkeiten des Kindes auf Ebene der Kompetenzstufen ausgeprägt sind. Informationen zu den Kompetenzstufen finden Sie{' '}
            <a href="https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell" target="_blank" className="underline hover:text-mainBlue" rel="noreferrer">
              hier
            </a>
            .
          </p>
        </div>
        <div className="max-w-6xl py-6 w-full grid grid-cols-1 gap-12 lg:gap-28">
          <div className="w-full space-y-4" ref={educationRef}>
            <h3 className="text-base font-semibold">Angaben zum Kind</h3>
            <div>
              <Select
                label="Bildungsbereich"
                onToggle={() => dispatch({ type: 'educationOptions' })}
                selected={makeOption(getEducation)(state?.education)}
                setSelected={(o) => {
                  dispatch({ type: 'education', payload: o.id });
                  setEducationError(false);
                }}
                options={Object.values(education).map(makeOption(getEducation))}
              />
              {educationError && <p className="mt-2 text-sm text-red-600">Feld erforderlich</p>}
            </div>
          </div>
          <div className="w-full space-y-4" ref={testsRef}>
            <h3 className="text-base font-semibold">{selfAssessment ? 'Selbsteinschätzung' : 'Testverfahren'}</h3>
            {selfAssessment ? (
              <div className="flex flex-col gap-y-6">
                <SelfAssessmentGroup
                  innerRef={readingRef}
                  colorClass="text-yellow-400"
                  name="Reading"
                  label="Lesen"
                  error={readingError}
                  disabled={disableReading}
                  checked={checkedReading}
                  setChecked={setCheckedReading}
                  setDisable={setDisableReading}
                  selectors={[
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Basiskompetenzen Lesen',
                      options: 'readingK1Options',
                      type: 'readingK1',
                      value: state?.readingK1Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Erstes Lesen',
                      options: 'readingK2Options',
                      type: 'readingK2',
                      value: state?.readingK2Results,
                      levels: competenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Lesen',
                      options: 'readingK3Options',
                      type: 'readingK3',
                      value: state?.readingK3Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Angewandtes und flexibles Lesen',
                      options: 'readingK4Options',
                      type: 'readingK4',
                      value: state?.readingK4Results,
                      levels: competenceLevels,
                    },
                  ]}
                />
                <SelfAssessmentGroup
                  innerRef={writingRef}
                  colorClass="text-green-400"
                  name="Writing"
                  label="Schreiben"
                  error={writingError}
                  disabled={disableWriting}
                  checked={checkedWriting}
                  setChecked={setCheckedWriting}
                  setDisable={setDisableWriting}
                  selectors={[
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Basiskompetenzen Schreiben',
                      options: 'writingK1Options',
                      type: 'writingK1',
                      value: state?.writingK1Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Erstes Schreiben',
                      options: 'writingK2Options',
                      type: 'writingK2',
                      value: state?.writingK2Results,
                      levels: competenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Schreiben',
                      options: 'writingK3Options',
                      type: 'writingK3',
                      value: state?.writingK3Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Angewandtes und flexibles Schreiben',
                      options: 'writingK4Options',
                      type: 'writingK4',
                      value: state?.writingK4Results,
                      levels: competenceLevels,
                    },
                  ]}
                />
                <SelfAssessmentGroup
                  innerRef={countingRef}
                  colorClass="text-blue-400"
                  name="Counting"
                  label="Rechnen"
                  error={countingError}
                  disabled={disableCounting}
                  checked={checkedCounting}
                  setChecked={setCheckedCounting}
                  setDisable={setDisableCounting}
                  selectors={[
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Basiskompetenzen Rechnen',
                      options: 'countingK1Options',
                      type: 'countingK1',
                      value: state?.countingK1Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Erstes Rechnen',
                      options: 'countingK2Options',
                      type: 'countingK2',
                      value: state?.countingK2Results,
                      levels: competenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Rechnen',
                      options: 'countingK3Options',
                      type: 'countingK3',
                      value: state?.countingK3Results,
                      levels: specifiedCompetenceLevels,
                    },
                    {
                      link: 'https://londi.de/lehrkr%C3%A4fte/lehrkr%C3%A4fte-hilfssystem#das-londi-kompetenzstufenmodell',
                      label: 'Angewandtes und flexibles Rechnen',
                      options: 'countingK4Options',
                      type: 'countingK4',
                      value: state?.countingK4Results,
                      levels: competenceLevels,
                    },
                  ]}
                />
              </div>
            ) : (
              <TestProcedure
                selectCountingGroup={selectCountingGroup}
                setSelectCountingGroup={setSelectCountingGroup}
                selectReadingGroup={selectReadingGroup}
                setSelectReadingGroup={setSelectReadingGroup}
                selectWritingGroup={selectWritingGroup}
                setSelectWritingGroup={setSelectWritingGroup}
                readingError={readingError}
                writingError={writingError}
                countingError={countingError}
                testProcedure={testProcedure}
                checkedReading={checkedReading}
                setCheckedReading={setCheckedReading}
                disableReading={disableReading}
                setDisableReading={setDisableReading}
                checkedWriting={checkedWriting}
                setCheckedWriting={setCheckedWriting}
                disableWriting={disableWriting}
                setDisableWriting={setDisableWriting}
                checkedCounting={checkedCounting}
                setCheckedCounting={setCheckedCounting}
                disableCounting={disableCounting}
                setDisableCounting={setDisableCounting}
                MandatoryError={MandatoryError}
                testWrapperRef={testsRef}
                readingRef={readingRef}
                writingRef={writingRef}
                countingRef={countingRef}
              />
            )}
            {MandatoryError && <Alert type="error">Angabe in mindestens einem Bereich erforderlich</Alert>}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 my-12 w-full sm:w-auto px-4 sm:px-0">
          <Button size="lg" onClick={onSubmit} disabled={testProcedure && !checkedReading && !checkedWriting && !checkedCounting}>
            Weiter
          </Button>
          {testProcedure && (
            <Button size="lg" className="whitespace-normal sm:whitespace-nowrap" onClick={onSkip} disabled={checkedReading || checkedWriting || checkedCounting}>
              Ohne Testergebnisse weiter
            </Button>
          )}
        </div>
      </Container>
    </>
  );
}

export default PromotionTest;

function makeOption(handler) {
  return (v) => {
    if (v == null) return undefined;
    return { id: v, name: handler(v) };
  };
}
